import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
import { X, MapPin, Star, UserPlus, Check, Loader2 } from 'lucide-react';
import showToast from '../../ShowToast';

// --- IMPORT PROFILE COMPONENT ---
import BusinessProfilePureCSS from '../../../profiles/BusinessProfilePureCSS';
import VerifiedBadge from '../../VerifiedBadge/VerifiedBadge';

// Styles
const styles = {
    overlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    modal: { position: 'relative', background: 'white', width: '700px', maxWidth: '95vw', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', maxHeight: '85vh' },
    header: { padding: '1.5rem', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#F8FAFC' },
    closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#64748B' },
    body: { padding: '1.5rem', overflowY: 'auto', flex: 1 },
    grid: { display: 'flex', flexDirection: 'column', gap: '1rem' },
    card: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.25rem', border: '1px solid #E2E8F0', borderRadius: '8px', transition: 'all 0.2s' },
    avatar: { width: '56px', height: '56px', borderRadius: '50%', objectFit: 'cover', border: '1px solid #E2E8F0', background: '#F1F5F9' },
    btnInvite: { background: '#0F172A', color: 'white', border: 'none', padding: '0.6rem 1.2rem', borderRadius: '6px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' },
    btnInvited: { background: '#F0FDF4', color: '#16A34A', border: '1px solid #BBF7D0', padding: '0.6rem 1.2rem', borderRadius: '6px', fontWeight: '600', cursor: 'default', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' },
    btnProfile: { background: 'white', border: '1px solid #E2E8F0', color: '#0F172A', padding: '0.6rem 1rem', borderRadius: '6px', fontWeight: '500', cursor: 'pointer', marginRight: '8px' }
};

const BrowseProsModal = ({ isOpen, onClose, trade, professionId, postcode, taskId, onInviteSent, jobId, leads }) => {
    const [pros, setPros] = useState([]);
    const [loading, setLoading] = useState(true);
    const [invitedIds, setInvitedIds] = useState(new Set());
    const [toastMessage, setToastMessage] = useState(null);

    // Sync leads from parent to local invited state
    useEffect(() => {
        if (leads && Array.isArray(leads)) {
            const existingIds = new Set(leads.map(l => parseInt(l.businessId || l.BusinessId)));
            setInvitedIds(prev => {
                // Merge existing local invites with server invites (union)
                const merged = new Set([...prev, ...existingIds]);
                return merged;
            });
        }
    }, [leads]);

    // --- PROFILE STATE ---
    const [profileBusinessId, setProfileBusinessId] = useState(null);

    useEffect(() => {
        if (isOpen) {
            fetchPros();
        }
    }, [isOpen, trade, professionId, postcode]);

    const fetchPros = async () => {
        setLoading(true);

        // Data cleaning (Postcode extraction)
        const cleanPostcode = postcode ? (postcode.match(/\d{4}/) ? postcode.match(/\d{4}/)[0] : postcode) : "";

        if (!cleanPostcode) {
            setLoading(false);
            return;
        }

        try {
            const params = new URLSearchParams();
            params.append('postcode', cleanPostcode);
            params.append('radius', '50');

            if (professionId) params.append('professionId', professionId);
            if (taskId) params.append('taskId', taskId);

            if (trade) params.append('trade', trade);
            else if (!professionId) params.append('trade', "General");

            const url = `${API_BASE_URL}/api/Jobs/find-local-pros?${params.toString()}`;
            console.log("url invite pros: ", url);
            const response = await axios.get(url);

            const data = Array.isArray(response.data) ? response.data : (response.data.result || []);
            console.log("pros = ", data);
            setPros(data);
        } catch (error) {
            console.error('Error fetching pros:', error);
        } finally {
            setLoading(false);
        }
    };

    // In-modal toast with auto-dismiss
    const showModalToast = (message) => {
        setToastMessage(message);
        setTimeout(() => setToastMessage(null), 4000);
    };

    const handleInvite = async (businessId, locationId) => {
        try {
            await axios.post(`${API_BASE_URL}/api/Jobs/invite-pro`, {
                taskId: parseInt(taskId),
                businessId: parseInt(businessId),
                locationId: parseInt(locationId),
                jobId: parseInt(jobId)
            });

            setInvitedIds(prev => new Set(prev).add(businessId));
            showModalToast('Invite sent! The professional will be notified to submit a quote.');

            if (onInviteSent) onInviteSent();
        } catch (error) {
            console.error('Error inviting pro:', error);
            showModalToast('Failed to invite professional.');
        }
    };

    // --- UPDATED VIEW PROFILE HANDLER ---
    const handleViewProfile = (businessId) => {
        setProfileBusinessId(businessId);
    };

    if (!isOpen) return null;

    return (
        <>
            <div style={styles.overlay} onClick={onClose}>
                <div style={styles.modal} onClick={e => e.stopPropagation()}>

                    <div style={styles.header}>
                        <div>
                            <h3 style={{margin:0, color:'#0F172A', fontSize:'1.25rem'}}>
                                Invite {trade || 'Professionals'}
                            </h3>
                            <p style={{margin:'4px 0 0 0', color:'#64748B', fontSize:'0.9rem'}}>
                                Found {pros.length} professionals near {postcode}
                            </p>
                        </div>
                        <button onClick={onClose} style={styles.closeBtn}><X size={24} /></button>
                    </div>

                    {/* Info banner */}
                    <div style={{
                        padding: '10px 1.5rem',
                        background: '#EFF6FF',
                        borderBottom: '1px solid #BFDBFE',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '0.85rem',
                        color: '#1E40AF',
                        lineHeight: '1.4'
                    }}>
                        <span style={{ flexShrink: 0 }}>&#8505;&#65039;</span>
                        <span>Inviting a professional sends them an alert to review your job details. Your exact address and phone number remain hidden.</span>
                    </div>

                    <div style={styles.body}>
                        {loading ? (
                            <div style={{display:'flex', justifyContent:'center', padding:'3rem'}}>
                                <Loader2 size={32} className="animate-spin text-blue-600" />
                            </div>
                        ) : pros.length === 0 ? (
                            <div style={{textAlign:'center', padding:'3rem', color:'#64748B'}}>
                                <p>No local professionals found for this criteria.</p>
                            </div>
                        ) : (
                            <div style={styles.grid}>
                                {pros.map(pro => (
                                    <div key={pro.businessId} style={styles.card}>
                                        <div style={{display:'flex', gap:'1rem', alignItems:'center'}}>
                                            <img
                                                src={pro.avatarUrl || "/placeholder-business.png"}
                                                alt={pro.businessName}
                                                style={styles.avatar}
                                                onError={(e) => {
                                                    if (!e.target.src.includes("placeholder-business.png")) {
                                                        e.target.onerror = null;
                                                        e.target.src="/placeholder-business.png";
                                                    }
                                                }}
                                            />
                                            <div>
                                                <h4 style={{margin:'0 0 4px 0', fontSize:'1.1rem', color:'#0F172A', fontWeight:600}}>
                                                    {pro.businessName}
                                                    {pro.isVerified && <VerifiedBadge size={18} />}
                                                </h4>
                                                <div style={{display:'flex', alignItems:'center', gap:'10px', fontSize:'0.85rem', color:'#64748B'}}>
                                                    <span style={{display:'flex', alignItems:'center', gap:'4px'}}>
                                                        <Star size={14} fill="#F59E0B" color="#F59E0B"/>
                                                        {pro.rating && pro.reviewCount > 0
                                                            ? <>{pro.rating.toFixed(1)} ({pro.reviewCount})</>
                                                            : <span style={{color:'#3B82F6'}}>New Pro - reviews coming soon</span>
                                                        }
                                                    </span>
                                                    <span style={{display:'flex', alignItems:'center', gap:'4px'}}>
                                                        <MapPin size={14}/> {pro.suburb || (pro.distanceKm ? `${pro.distanceKm} km away` : 'Local')}
                                                    </span>
                                                </div>
                                                {pro.description && (
                                                    <p style={{margin:'4px 0 0 0', fontSize:'0.8rem', color:'#94A3B8', maxWidth:'350px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>
                                                        {pro.description}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        <div style={{display:'flex', alignItems:'center'}}>
                                            <button
                                                style={styles.btnProfile}
                                                onClick={() => handleViewProfile(pro.businessId)}
                                            >
                                                Profile
                                            </button>

                                            <button
                                                onClick={() => handleInvite(pro.businessId, pro.locationId)}
                                                disabled={invitedIds.has(pro.businessId)}
                                                style={invitedIds.has(pro.businessId) ? styles.btnInvited : styles.btnInvite}
                                            >
                                                {invitedIds.has(pro.businessId) ? (
                                                    <><Check size={16} /> Invited</>
                                                ) : (
                                                    <><UserPlus size={16} /> Request Quote</>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* In-modal toast notification */}
                    {toastMessage && (
                        <div style={{
                            position: 'absolute',
                            bottom: '16px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: '#0F172A',
                            color: 'white',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                            zIndex: 10,
                            maxWidth: '90%',
                            textAlign: 'center',
                            animation: 'fadeInUp 0.3s ease-out'
                        }}>
                            {toastMessage}
                        </div>
                    )}
                </div>
            </div>

            {/* --- RENDER BUSINESS PROFILE PANEL --- */}
            <BusinessProfilePureCSS
                isOpen={!!profileBusinessId}
                onClose={() => setProfileBusinessId(null)}
                businessId={profileBusinessId}
                useIDTYPE={"BID"}
            />
        </>
    );
};

export default BrowseProsModal;
